import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Crown, Trophy, Gem, Sparkles, Gift, ArrowRight, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

// ------------------------------
// Types
// ------------------------------
interface UserPointsView {
  user_id: string;
  available_points: number;
  lifetime_points: number;
  tier: "silver" | "gold" | "platinum" | "diamond" | string;
  tier_points: number;
  tier_min: number;
  cashback_bps: number;
  perks: any;
  next_tier_min: number | null;
}

interface LedgerRow {
  id: string;
  kind: "earn" | "redeem" | "expire" | "adjust";
  source: string;
  description: string | null;
  points: number;
  points_sign: 1 | -1;
  provider: string | null;
  booking_id: string | null;
  created_at: string;
}

interface RewardItem {
  id: string;
  title: string;
  partner: string | null;
  points_cost: number;
  stock: number | null;
  is_active: boolean;
  meta: any;
}

// ------------------------------
// Helpers
// ------------------------------
function classNames(...xs: (string | false | undefined | null)[]) {
  return xs.filter(Boolean).join(" ");
}

function tierMeta(tier: string | undefined) {
  switch (tier) {
    case "diamond":
      return { label: "Diamond", icon: Gem, ring: "from-cyan-400 to-fuchsia-500" };
    case "platinum":
      return { label: "Platinum", icon: Trophy, ring: "from-slate-200 to-purple-300" };
    case "gold":
      return { label: "Gold", icon: Crown, ring: "from-amber-300 to-yellow-500" };
    default:
      return { label: "Silver", icon: Sparkles, ring: "from-zinc-200 to-slate-300" };
  }
}

function formatDate(dt: string) {
  return new Date(dt).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// ------------------------------
// UI Components
// ------------------------------
const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ className, children }) => (
  <div className={classNames("rounded-2xl bg-card border shadow-lg p-6", className)}>
    {children}
  </div>
);

const Stat: React.FC<{ label: string; value: string | number; sub?: string }> = ({ label, value, sub }) => (
  <div className="flex flex-col">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-3xl font-bold">{value}</span>
    {sub && <span className="text-xs text-muted-foreground mt-1">{sub}</span>}
  </div>
);

const ProgressBar: React.FC<{ value: number; max: number }> = ({ value, max }) => {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <div className="w-full h-3 rounded-full bg-secondary overflow-hidden">
      <div className="h-full bg-gradient-to-r from-primary to-accent transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
};

// ------------------------------
// Main Component
// ------------------------------
export default function PointsDashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vp, setVp] = useState<UserPointsView | null>(null);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [rewards, setRewards] = useState<RewardItem[]>([]);
  const [redeeming, setRedeeming] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Auth gate
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?next=/dashboard/points");
    }
  }, [user, authLoading, navigate]);

  // Fetch data
  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    Promise.all([
      supabase.rpc("get_user_points", { p_user_id: user.id }),
      supabase
        .from("points_ledger")
        .select("id, kind, source, description, points, points_sign, provider, booking_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase.from("rewards").select("*").eq("is_active", true).order("points_cost", { ascending: true }),
    ])
      .then(([vpRes, ledRes, rewRes]) => {
        if (vpRes.data && vpRes.data.length > 0) {
          setVp(vpRes.data[0] as UserPointsView);
        }
        if (ledRes.data) setLedger(ledRes.data as LedgerRow[]);
        if (rewRes.data) setRewards(rewRes.data as RewardItem[]);
      })
      .catch((err) => {
        console.error("Error fetching points data:", err);
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  const meta = tierMeta(vp?.tier);
  const nextTarget = vp?.next_tier_min ?? null;
  const progressVal = useMemo(() => {
    if (!vp) return 0;
    const currentBase = vp.tier_min || 0;
    const target = vp.next_tier_min ?? currentBase;
    const span = Math.max(1, target - currentBase);
    const progressed = Math.max(0, vp.tier_points - currentBase);
    return Math.min(span, progressed);
  }, [vp]);

  async function refreshAll() {
    if (!user?.id) return;
    setRefreshing(true);
    const { data: vpData } = await supabase.rpc("get_user_points", { p_user_id: user.id });
    if (vpData && vpData.length > 0) setVp(vpData[0] as UserPointsView);
    const led = await supabase
      .from("points_ledger")
      .select("id, kind, source, description, points, points_sign, provider, booking_id, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    if (led.data) setLedger(led.data as LedgerRow[]);
    setRefreshing(false);
  }

  async function handleRedeem(reward: RewardItem) {
    if (!user?.id || !vp) return;
    if (vp.available_points < reward.points_cost) {
      alert("Not enough points to redeem this reward.");
      return;
    }
    const confirmRedeem = window.confirm(`Redeem ${reward.points_cost} points for "${reward.title}"?`);
    if (!confirmRedeem) return;
    setRedeeming(reward.id);

    // 1) create redemption record
    const { data: rr, error: rrErr } = await supabase
      .from("reward_redemptions")
      .insert({ reward_id: reward.id, user_id: user.id, points_spent: reward.points_cost, status: "pending" })
      .select("id")
      .single();
    if (rrErr) {
      console.error(rrErr);
      alert("Could not start redemption.");
      setRedeeming(null);
      return;
    }

    // 2) debit points via RPC to ensure atomic balance change
    const { error: rpcErr } = await supabase.rpc("redeem_points", {
      p_user: user.id,
      p_points: reward.points_cost,
      p_desc: `Reward: ${reward.title}`,
    });

    if (rpcErr) {
      console.error(rpcErr);
      alert("Could not redeem points (RPC).");
      // rollback redemption record if needed
      await supabase.from("reward_redemptions").update({ status: "failed" }).eq("id", rr.id);
      setRedeeming(null);
      return;
    }

    // 3) mark redemption fulfilled
    await supabase.from("reward_redemptions").update({ status: "fulfilled" }).eq("id", rr.id);

    // 4) refresh balances
    await refreshAll();
    setRedeeming(null);
    alert("Redeemed! Check your email or Rewards tab for details.");
  }

  if (authLoading || loading || !vp) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="animate-spin" /> Loading your points…
        </div>
      </div>
    );
  }

  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-primary/10 ring-2 ring-primary/20">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Flexaura Points</h1>
              <p className="text-muted-foreground">Track your balance, tier, and rewards.</p>
            </div>
          </div>
          <button 
            onClick={refreshAll} 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors"
          >
            <RefreshCw className={classNames("w-4 h-4", refreshing && "animate-spin")} /> Refresh
          </button>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <div className="flex items-center justify-between">
              <Stat label="Available Points" value={vp.available_points.toLocaleString()} />
              <div className="text-4xl">💎</div>
            </div>
          </Card>
          <Card>
            <Stat 
              label="Current Tier" 
              value={tierMeta(vp.tier).label} 
              sub={`Tier points (last 12 mo): ${vp.tier_points.toLocaleString()}`} 
            />
          </Card>
          <Card>
            {vp.next_tier_min ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Progress to next tier</span>
                  <span className="text-sm font-medium">
                    {progressVal.toLocaleString()} / {(vp.next_tier_min - vp.tier_min).toLocaleString()}
                  </span>
                </div>
                <ProgressBar value={progressVal} max={(vp.next_tier_min - vp.tier_min) || 1} />
              </div>
            ) : (
              <div className="flex items-center h-full text-lg">You are at the top tier 🎉</div>
            )}
          </Card>
        </div>

        {/* Ledger & Rewards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ledger */}
          <div className="lg:col-span-2">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Recent Activity</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-muted-foreground border-b">
                    <tr>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Source</th>
                      <th className="py-2 pr-4">Description</th>
                      <th className="py-2 pr-4 text-right">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-muted-foreground">
                          No activity yet. Make a booking to earn points.
                        </td>
                      </tr>
                    )}
                    {ledger.map((row) => (
                      <tr key={row.id} className="border-b border-border">
                        <td className="py-3 pr-4 whitespace-nowrap">{formatDate(row.created_at)}</td>
                        <td className="py-3 pr-4 capitalize">{row.kind}</td>
                        <td className="py-3 pr-4 capitalize">{row.source}</td>
                        <td className="py-3 pr-4">{row.description ?? "—"}</td>
                        <td className={classNames(
                          "py-3 pr-4 text-right font-medium",
                          row.points_sign === 1 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                        )}>
                          {row.points_sign === 1 ? "+" : "-"}{row.points.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Rewards */}
          <div className="lg:col-span-1">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Gift className="w-5 h-5"/> Rewards
                </h2>
                <a href="/rewards" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                  View all <ArrowRight className="w-4 h-4"/>
                </a>
              </div>
              <div className="space-y-3">
                {rewards.slice(0, 6).map((rw) => (
                  <div key={rw.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/50 border">
                    <div>
                      <div className="font-medium">{rw.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {rw.partner ?? "Flexaura"} • {rw.points_cost.toLocaleString()} pts
                      </div>
                    </div>
                    <button
                      onClick={() => handleRedeem(rw)}
                      disabled={redeeming === rw.id || (vp?.available_points ?? 0) < rw.points_cost}
                      className={classNames(
                        "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        (vp?.available_points ?? 0) >= rw.points_cost 
                          ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                          : "bg-secondary cursor-not-allowed opacity-50"
                      )}
                    >
                      {redeeming === rw.id ? "Redeeming…" : "Redeem"}
                    </button>
                  </div>
                ))}
                {rewards.length === 0 && (
                  <div className="text-muted-foreground text-sm">No rewards available right now.</div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
